import React, { Fragment, FunctionComponent, h } from "preact"; // eslint-disable-line @typescript-eslint/no-unused-vars
import { useEffect, useState } from "preact/hooks";

import cx from "classnames";

import SVGIcon from "../../../components/SVGIcon"; // eslint-disable-line @typescript-eslint/no-unused-vars
import Loading from "../../../components/Loading";

export interface IFilterCategory {
  id: string;
  name: string;
  selectable: boolean;
  children?: IFilterCategory[];
}

export interface IFilters {
  filters?: IFilterCategory[];
  resetFilters: boolean;
  preSelectedFilters: string[];
  filterParams?: string[]; // Use this for filtering based on preselected filters - i.e. from the URL
  className?: string;
  isMobile?: boolean;
  loading?: boolean;
  settings: any;
  handleFilters: (newFilters: string[]) => void;
}

const Filters: FunctionComponent<IFilters> = ({
  filters,
  resetFilters,
  handleFilters,
  loading,
  preSelectedFilters,
  className,
  isMobile = false,
  settings,
}) => {
  if (!filters) {
    return null;
  }

  const [activeFilters, setActiveFilters] = useState<string[]>(preSelectedFilters);
  const [expandedFilters, setExpandedFilters] = useState<string[]>([]);
  const [shownFilters, setShownFilters] = useState<string[]>([]);
  const maxShownFilters = settings.maxFilters ?? 3;

  useEffect(() => {
    if (resetFilters) {
      setActiveFilters([]); // On change of this var, reset filters. This happens on switching a tab or searching for a new query
    }
  }, [resetFilters]);

  useEffect(() => {
    if (preSelectedFilters) {
      setActiveFilters(preSelectedFilters);
    }
  }, [preSelectedFilters]);

  const newCategories = (id: string, filterList: string[]): string[] => {
    const fil = filterList.slice();
    const index = fil.indexOf(id);
    const active = index > -1;
    if (active) {
      fil.splice(index, 1);
      return fil;
    }
    return fil.concat(id);
  };

  const toggleExpand = (id: string) => {
    let newExpandedFilters = expandedFilters.slice();
    const alreadyExpanded = newExpandedFilters.find((f) => f === id);
    if (alreadyExpanded) {
      newExpandedFilters = newExpandedFilters.filter((f) => f !== id);
    } else {
      newExpandedFilters.push(id);
    }
    setExpandedFilters(newExpandedFilters);
  };

  const toggleSelect = (id: string) => {
    const newList = newCategories(id, activeFilters);
    handleFilters(newList);
    setActiveFilters(newList);
  };

  const clearFilters = () => {
    setExpandedFilters([]);
    handleFilters([]);
    setActiveFilters([]);
    setShownFilters([]);
  };

  const toggleShowFilters = (id: string) => {
    let shownFilterList = shownFilters.slice();
    const alreadyShown = shownFilterList.find((c) => c === id);
    if (alreadyShown) {
      shownFilterList = shownFilterList.filter((c) => c !== id);
    } else {
      shownFilterList.push(id);
    }
    setShownFilters(shownFilterList);
  };

  const renderList = (fil: IFilterCategory[], topLevel = false, parentId: string) => {
    const isShown = shownFilters.find((c) => c === parentId);
    let showMoreButton = fil.length > maxShownFilters && !topLevel;
    return (
      <ul
        className={cx(
          "search-filter__list",
          { "search-filter__list-primary": topLevel },
          { "search-filter__list-secondary": !topLevel },
          { "search-filter__list--loading": loading }
        )}
      >
        {fil.map((cat, index) => {
          const { name, id, selectable, children } = cat;
          const showHeading = topLevel && !selectable; // Static heading
          const showButton = !topLevel && !selectable; // Button - dropdown style - agreed that won't cover case where a top level item is selectable
          const showInput = !topLevel && selectable; // Checkbox filter
          const expanded = showButton && !!expandedFilters.find((c) => c === id);
          const hidden = index >= maxShownFilters && !isShown && !topLevel;
          const collapsed = showButton && !expanded;
          const active = !!activeFilters.find((c) => c === id);
          const inputId = `${id}${isMobile ? "-m" : ""}`;
          return (
            !hidden && (
              <li
                className={cx(
                  "search-filter__list-item",
                  { "fancy-checkbox": showInput },
                  { "search-filter__group--open": expanded },
                  { "search-filter__group--closed": collapsed }
                )}
              >
                {showHeading && <h3 className="search-filter__list-heading">{name}</h3>}

                {showButton && (
                  <button
                    id={id}
                    className={cx(
                      "search-filter__list-action trigger search-filter__trigger",
                      { "trigger--open": expanded },
                      { "trigger--closed": collapsed }
                    )}
                    onClick={() => toggleExpand(cat.id)}
                  >
                    {name}
                  </button>
                )}

                {showInput && (
                  <Fragment>
                    <input
                      className="search-filter__checkbox"
                      id={inputId}
                      type="checkbox"
                      checked={active}
                      onChange={() => {
                        toggleSelect(cat.id);
                      }}
                    />
                    <label
                      htmlFor={inputId}
                      className="search-filter__list-action"
                    >
                      {name}
                    </label>
                  </Fragment>
                )}
                {children && renderList(children, false, id)}
              </li>
            )
          );
        })}
        {showMoreButton && (
          <Fragment>
            <li class="search-filter__list-item">
              <button
                className={cx(
                  "unbutton",
                  "search-filter__list-action",
                  "search-filter__list-control",
                  `search-filter__list-control--${!isShown ? "more" : "less"}`
                )}
                onClick={() => {
                  toggleShowFilters(parentId);
                }}
              >
                <SVGIcon
                  name={!isShown ? "chevron--down" : "chevron--up"}
                  className={cx(
                    "chevron-icon",
                    `chevron-icon--${!isShown ? "down" : "up"}`,
                    "icon--sm",
                    "search-filter__list-control-icon"
                  )}
                />
                {!isShown ? "Show more" : "Show less"}
              </button>
            </li>
          </Fragment>
        )}
      </ul>
    );
  };

  return (
    <div
      className={cx("search-filter", className, {
        "search-filter--mobile": isMobile,
      })}
    >
      {
        <div className={cx("search-filter__header", { "search-filter__header": loading })}>
          {!isMobile && <h2 className="h3 search-filter__title">Filters</h2>}
          {
            <button
              class="unbutton search-filter__clear"
              onClick={() => clearFilters()}
            >
              Clear filters
            </button>
          }
        </div>
      }
      {loading && (
        <Loading
          text=" "
          center={false}
          centerHorizontally={true}
          pad={true}
          overlay={true}
        />
      )}
      {renderList(filters, true, "")}
    </div>
  );
};

export default Filters;
